// ============================================================
// Registre des PAGES PRODUIT (/shop/:slug) — Inside Panama
// ------------------------------------------------------------
// Chaque produit a sa page de vente dédiée, rendue par
// src/pages/ProductPage.jsx. Le flag `visible` pilote l'affichage :
//   visible: false  → page accessible par URL directe (aperçu) mais
//                      NON indexée (noindex) et NON listée dans /shop.
//   visible: true   → indexée + liée depuis la grille /shop.
// → On peut donc « tout construire » sans « tout afficher » : on bascule
//   visible:true produit par produit quand le produit est prêt (fichier
//   livré, lien de paiement actif, fiche relue).
//
// `buy` : { provider, key } → résolu par ProductPage
//   - gumroad      → getPaymentLink(key)  (src/data/paymentLinks.js)
//   - lemonsqueezy → checkoutUrl(key)     (src/data/lemonsqueezyProducts.js)
//   Si le lien est null (pas encore actif) → CTA « Bientôt disponible ».
//
// i18n : name / tagline / bullets sont des objets {fr,en,es}. `pick()`
// retombe sur fr si une langue manque (les fiches sont rédigées en FR
// d'abord ; on traduit à l'activation).
// ============================================================

export const PRODUCTS = [
  {
    slug: 'panama-relocation-toolkit',
    buy: { provider: 'gumroad', key: 'toolkit' },
    price: 49,
    tier: 'toolkit',
    cover: '/product-covers/cover-toolkit.svg',
    visible: false,
    focusKeyword: { fr: 'guide expatriation panama', en: 'move to panama guide', es: 'guía mudarse a panamá' },
    name: {
      fr: 'Panama Relocation Toolkit',
      en: 'Panama Relocation Toolkit',
      es: 'Panama Relocation Toolkit',
    },
    tagline: {
      fr: "Le guide complet 2026 pour s'expatrier au Panama : visa Friendly Nations, banque, fiscalité territoriale, budget réel — réuni en un seul dossier.",
      en: 'The complete 2026 guide to moving to Panama: Friendly Nations visa, banking, territorial tax, real budget — all in one file.',
      es: 'La guía completa 2026 para mudarte a Panamá: visa Países Amigos, banca, fiscalidad territorial, presupuesto real — todo en un solo dossier.',
    },
    bullets: {
      fr: [
        'La marche à suivre visa Friendly Nations, étape par étape.',
        'Ouvrir un compte bancaire panaméen : quelles banques, quels documents.',
        'La fiscalité territoriale expliquée simplement (0 % sur le revenu étranger).',
        'Le budget mensuel réel par profil — célibataire, couple, famille.',
      ],
      en: [
        'The Friendly Nations visa roadmap, step by step.',
        'Opening a Panamanian bank account: which banks, which documents.',
        'Territorial tax explained simply (0% on foreign income).',
        'The real monthly budget by profile — single, couple, family.',
      ],
      es: [
        'La hoja de ruta del visa Países Amigos, paso a paso.',
        'Abrir una cuenta bancaria panameña: qué bancos, qué documentos.',
        'La fiscalidad territorial explicada simple (0 % sobre renta extranjera).',
        'El presupuesto mensual real por perfil — soltero, pareja, familia.',
      ],
    },
  },
  {
    slug: 'checklist-visa-friendly-nations',
    buy: { provider: 'gumroad', key: 'visa' },
    price: 19,
    tier: 'toolkit',
    cover: '/product-covers/cover-visa.svg',
    visible: false,
    focusKeyword: { fr: 'checklist visa friendly nations', en: 'friendly nations visa checklist', es: 'checklist visa países amigos' },
    name: {
      fr: 'Checklist Visa Friendly Nations + Templates',
      en: 'Friendly Nations Visa Checklist + Templates',
      es: 'Checklist Visa Países Amigos + Plantillas',
    },
    tagline: {
      fr: "La checklist officielle des documents, les modèles d'emails et la chronologie 12 mois pour décrocher ta résidence sans rater une étape.",
      en: 'The official document checklist, email templates and 12-month timeline to land your residency without missing a step.',
      es: 'La checklist oficial de documentos, plantillas de emails y cronología de 12 meses para obtener tu residencia sin saltarte un paso.',
    },
    bullets: {
      fr: [
        'La liste exacte des documents (apostille, traductions, RDV Migración).',
        'Les modèles d\'emails FR/ES pour ton avocat et la banque.',
        'La chronologie réaliste sur 12 mois, jalon par jalon.',
        'Les pièges qui font rejeter un dossier — et comment les éviter.',
      ],
      en: [
        'The exact document list (apostille, translations, Migración appointment).',
        'FR/ES email templates for your lawyer and the bank.',
        'A realistic 12-month timeline, milestone by milestone.',
        'The mistakes that get a file rejected — and how to avoid them.',
      ],
      es: [
        'La lista exacta de documentos (apostilla, traducciones, cita Migración).',
        'Plantillas de emails FR/ES para tu abogado y el banco.',
        'Una cronología realista de 12 meses, hito por hito.',
        'Los errores que hacen rechazar un expediente — y cómo evitarlos.',
      ],
    },
  },
  {
    slug: 'le-contador-panameen',
    buy: { provider: 'gumroad', key: 'contador' },
    price: 29,
    tier: 'memo',
    cover: null,
    visible: false,
    focusKeyword: { fr: 'contador panama', en: 'panama accountant', es: 'contador panamá' },
    name: {
      fr: 'Le contador panaméen — trouver, briefer, auditer',
      en: 'The Panama Accountant — find, brief, audit',
      es: 'El contador panameño — encontrar, instruir, auditar',
    },
    tagline: {
      fr: 'Gérer ton comptable panaméen sans te faire avoir : où le trouver, les tarifs réels, les 3 questions-filtre, les red flags et les templates de brief mensuel.',
      en: 'Manage your Panamanian accountant without getting burned: where to find one, real rates, the 3 filter questions, red flags and monthly-brief templates.',
      es: 'Gestiona a tu contador panameño sin que te engañen: dónde encontrarlo, tarifas reales, las 3 preguntas filtro, red flags y plantillas de informe mensual.',
    },
    bullets: {
      fr: [
        'Où trouver un contador sérieux (CCPAP) et les tarifs réels $80-300/mois.',
        'Les 3 questions-filtre qui révèlent s\'il maîtrise le régime territorial.',
        'La checklist des 12 documents à fournir chaque mois.',
        'Les red flags + un template de brief mensuel prêt à envoyer.',
      ],
      en: [
        'Where to find a serious accountant (CCPAP) and real rates $80-300/mo.',
        'The 3 filter questions that reveal if they grasp territorial tax.',
        'The checklist of 12 documents to provide each month.',
        'The red flags + a ready-to-send monthly brief template.',
      ],
      es: [
        'Dónde encontrar un contador serio (CCPAP) y tarifas reales $80-300/mes.',
        'Las 3 preguntas filtro que revelan si domina el régimen territorial.',
        'La checklist de los 12 documentos a entregar cada mes.',
        'Los red flags + una plantilla de informe mensual lista para enviar.',
      ],
    },
  },
  {
    slug: 'sante-panama-guide-expat',
    buy: { provider: 'gumroad', key: 'santePanama' },
    price: 19, // fix 2026-07-30 : $29 divergeait du prix Gumroad réel ($19, scripts/lib/gumroad-prices.json) — capté par tests/no-divergence.test.js dès que la clé santePanama a eu un vrai slug (elle était null avant, donc non comparable).
    tier: 'memo',
    cover: null,
    visible: false,
    focusKeyword: { fr: 'santé panama expat', en: 'panama healthcare expat', es: 'salud panamá expat' },
    name: {
      fr: 'Santé au Panama — le guide expat',
      en: 'Healthcare in Panama — the expat guide',
      es: 'Salud en Panamá — la guía expat',
    },
    tagline: {
      fr: 'Choisir entre assurance internationale et locale, trouver un médecin francophone, comprendre les coûts réels — hôpitaux, urgences, accouchement.',
      en: 'Choose between international and local insurance, find an English-speaking doctor, understand real costs — hospitals, emergencies, childbirth.',
      es: 'Elegir entre seguro internacional y local, encontrar un médico, entender los costos reales — hospitales, urgencias, parto.',
    },
    bullets: {
      fr: [
        'Assurance internationale vs locale : le comparatif chiffré par profil.',
        'Le carnet d\'adresses : médecins par spécialité, hôpitaux privés.',
        'Les coûts réels : consultation, urgences, accouchement à Paitilla.',
        'La procédure pas-à-pas pour souscrire sans mauvaise surprise.',
      ],
      en: [
        'International vs local insurance: the costed comparison by profile.',
        'The address book: doctors by specialty, private hospitals.',
        'Real costs: consultation, emergencies, childbirth at Paitilla.',
        'The step-by-step procedure to subscribe with no bad surprise.',
      ],
      es: [
        'Seguro internacional vs local: el comparativo con cifras por perfil.',
        'La agenda: médicos por especialidad, hospitales privados.',
        'Costos reales: consulta, urgencias, parto en Paitilla.',
        'El procedimiento paso a paso para contratar sin sorpresas.',
      ],
    },
  },
  {
    slug: 'encaisser-sans-banque-pro',
    buy: { provider: 'gumroad', key: 'bankPro' },
    price: 24,
    tier: 'memo',
    cover: null,
    visible: false,
    focusKeyword: { fr: 'encaisser sa panama', en: 'get paid panama company', es: 'cobrar sa panamá' },
    name: {
      fr: 'Encaisser sans banque pro — le mémo SA Panama',
      en: 'Get paid without a business bank — the Panama SA memo',
      es: 'Cobrar sin banco pro — el memo SA Panamá',
    },
    tagline: {
      fr: "Aucune banque panaméenne n'ouvre de compte pro avant tes premiers revenus. Voici le stack légal que 80 % des expats avec SA utilisent vraiment.",
      en: 'No Panamanian bank opens a business account before your first revenue. Here\'s the legal stack 80% of expats with an SA actually use.',
      es: 'Ningún banco panameño abre cuenta pro antes de tus primeros ingresos. Aquí está el stack legal que el 80 % de los expats con SA usa de verdad.',
    },
    bullets: {
      fr: [
        'Pourquoi le compte pro est bloqué — et ce qui marche à la place.',
        'Le stack : Tower retail, Stripe Atlas + LLC US, Mercury, Wise/Payoneer.',
        'Quelle option selon ton activité et ton volume.',
        'FAQ : 15 cas concrets d\'expats résolus.',
      ],
      en: [
        'Why the business account is blocked — and what works instead.',
        'The stack: Tower retail, Stripe Atlas + US LLC, Mercury, Wise/Payoneer.',
        'Which option fits your activity and volume.',
        'FAQ: 15 real expat cases, solved.',
      ],
      es: [
        'Por qué la cuenta pro está bloqueada — y qué funciona en su lugar.',
        'El stack: Tower retail, Stripe Atlas + LLC US, Mercury, Wise/Payoneer.',
        'Qué opción según tu actividad y volumen.',
        'FAQ: 15 casos reales de expats, resueltos.',
      ],
    },
  },
  {
    slug: 'optimisation-fiscale-panama',
    buy: { provider: 'gumroad', key: 'taxOptim' },
    price: 39,
    tier: 'premium',
    cover: null,
    visible: false,
    focusKeyword: { fr: 'optimisation fiscale panama', en: 'panama tax optimization', es: 'optimización fiscal panamá' },
    name: {
      fr: 'Optimisation fiscale au Panama — territorialité + structures',
      en: 'Tax optimization in Panama — territoriality + structures',
      es: 'Optimización fiscal en Panamá — territorialidad + estructuras',
    },
    tagline: {
      fr: 'La territorialité (0 % sur le revenu de source étrangère), les structures SA / Fundación / LLC+SA, le régime SEM — avec templates et checklist compliance.',
      en: 'Territoriality (0% on foreign-source income), SA / Fundación / LLC+SA structures, the SEM regime — with templates and a compliance checklist.',
      es: 'La territorialidad (0 % sobre renta de fuente extranjera), estructuras SA / Fundación / LLC+SA, el régimen SEM — con plantillas y checklist de compliance.',
    },
    bullets: {
      fr: [
        'La territorialité fiscale expliquée : ce qui est exonéré, ce qui ne l\'est pas.',
        'Les structures comparées : SA, Fundación, LLC US + SA panaméenne.',
        'Comparaison chiffrée Panama vs Dubaï / Singapour / Estonie.',
        'Templates + checklist compliance + FAQ 20 cas.',
      ],
      en: [
        'Territorial tax explained: what\'s exempt, what isn\'t.',
        'Structures compared: SA, Fundación, US LLC + Panamanian SA.',
        'Costed comparison Panama vs Dubai / Singapore / Estonia.',
        'Templates + compliance checklist + FAQ 20 cases.',
      ],
      es: [
        'La fiscalidad territorial explicada: qué está exento, qué no.',
        'Estructuras comparadas: SA, Fundación, LLC US + SA panameña.',
        'Comparación con cifras Panamá vs Dubái / Singapur / Estonia.',
        'Plantillas + checklist de compliance + FAQ 20 casos.',
      ],
    },
  },
  // ============================================================
  // 2026-06-26 — V1 + V2 du plan de déploiement (8 produits ajoutés).
  // visible:true → indexés + listés dans /shop. Fichiers Gumroad fournis,
  // permalinks confirmés FR/EN/ES via le manifest 2026-06-17.
  // ============================================================
  {
    slug: 'setup-expat-30-jours-panama',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'installation-d-expatrie-en-30-jours', en: 'setup-expat-30-jours', es: 'configuracion-expat-30-dias' } },
    price: 24,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-setup-expat-30-jours.png',
    visible: true,
    focusKeyword: { fr: 'installer panama 30 jours', en: 'panama 30 day setup', es: 'instalación panamá 30 días' },
    name: {
      fr: 'Setup expat 30 jours — Panama City',
      en: '30-Day Expat Setup — Panama City',
      es: 'Configuración expat 30 días — Ciudad de Panamá',
    },
    tagline: {
      fr: "Le plan jour-par-jour des 30 premiers jours à Panama City : démarches, logement, banque, transports, santé — pour arriver et avancer sans perdre une semaine.",
      en: 'The day-by-day plan for your first 30 days in Panama City: paperwork, housing, banking, transport, healthcare — to land and move without losing a week.',
      es: 'El plan día por día de tus primeros 30 días en Ciudad de Panamá: trámites, vivienda, banca, transporte, salud — para llegar y avanzar sin perder una semana.',
    },
    bullets: {
      fr: [
        'Le calendrier J1 → J30 avec les démarches dans le bon ordre (cédula, banque, abonnements).',
        'Les 10 items à régler la semaine 1, sans quoi tout bloque ensuite.',
        'Quartiers à explorer en priorité selon ton profil + comment visiter un appartement sans Uber surchargé.',
        'Modèles d\'emails FR/ES pour l\'avocat, le banquier, le bailleur.',
      ],
      en: [
        'The D1→D30 calendar with paperwork in the right order (cédula, bank, subscriptions).',
        'The 10 items to handle in week 1 — without which everything stalls later.',
        'Priority neighborhoods to scout by profile + how to visit a flat without Uber-spiking.',
        'FR/ES email templates for the lawyer, banker, landlord.',
      ],
      es: [
        'El calendario D1→D30 con los trámites en el orden correcto (cédula, banco, suscripciones).',
        'Los 10 ítems a resolver la semana 1, sin los cuales todo se atasca después.',
        'Barrios prioritarios a explorar según tu perfil + cómo visitar un apartamento sin Uber saturado.',
        'Plantillas de emails FR/ES para abogado, banquero, arrendador.',
      ],
    },
  },
  {
    slug: 'boquete-guide-expat',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'guide-expat-boquete', en: 'boquete-expat-guide', es: 'guia-de-expatriados-en-boquete' } },
    price: 14,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-boquete-expat-guide.png',
    visible: true,
    focusKeyword: { fr: 'boquete panama expat', en: 'boquete panama expat', es: 'boquete panamá expat' },
    name: {
      fr: 'Boquete — guide expat',
      en: 'Boquete — Expat Guide',
      es: 'Boquete — guía expat',
    },
    tagline: {
      fr: 'La ville d\'altitude la plus convoitée : climat de printemps toute l\'année, café, communauté expat la plus établie du pays. Tout ce qu\'il faut savoir avant de poser tes valises.',
      en: 'The most coveted highland town: spring-like weather year-round, coffee, the country\'s most established expat community. Everything to know before you settle in.',
      es: 'El pueblo de altura más codiciado: clima primaveral todo el año, café, la comunidad expat más establecida del país. Todo lo necesario antes de instalarte.',
    },
    bullets: {
      fr: [
        'Climat, micro-quartiers (Alto, Bajo, Volcancito) et leurs vraies différences.',
        'Coût de vie détaillé : loyer, courses, services à l\'année.',
        'La communauté expat — où la rencontrer, comment s\'intégrer.',
        'Santé, internet, services indispensables — testés sur place.',
      ],
      en: [
        'Climate, micro-neighborhoods (Alto, Bajo, Volcancito) and their real differences.',
        'Detailed cost of living: rent, groceries, year-round services.',
        'The expat community — where to meet them, how to integrate.',
        'Healthcare, internet, essential services — tested on the ground.',
      ],
      es: [
        'Clima, micro-barrios (Alto, Bajo, Volcancito) y sus diferencias reales.',
        'Costo de vida detallado: alquiler, compras, servicios al año.',
        'La comunidad expat — dónde encontrarla, cómo integrarse.',
        'Salud, internet, servicios esenciales — probados en el terreno.',
      ],
    },
  },
  {
    slug: 'vivre-au-chiriqui',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'vivre-a-chiriqui', en: 'living-in-chiriqui', es: 'vivir-chiriqui' } },
    price: 19,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-vivre-a-chiriqui.png',
    visible: true,
    focusKeyword: { fr: 'vivre au chiriqui panama', en: 'living in chiriqui panama', es: 'vivir chiriquí panamá' },
    name: {
      fr: 'Vivre au Chiriquí — David, Boquete & Highlands',
      en: 'Living in Chiriquí — David, Boquete & Highlands',
      es: 'Vivir en Chiriquí — David, Boquete y tierras altas',
    },
    tagline: {
      fr: "Toute la province verte décortiquée — David la pratique, Boquete l'altitude, Volcán le sauvage, Cerro Punta le frais. Choisir sa base selon ses critères réels.",
      en: 'The whole green province broken down — David the practical, Boquete the highland, Volcán the wild, Cerro Punta the cool. Choose your base on real criteria.',
      es: 'Toda la provincia verde desglosada — David práctico, Boquete de altura, Volcán salvaje, Cerro Punta fresco. Elige tu base con criterios reales.',
    },
    bullets: {
      fr: [
        'Les 4 bases comparées : climat, accès, services, communauté.',
        'Coûts loyer/vie pour chaque ville, à plusieurs profils.',
        'Santé, écoles, démarches : qui dépend de David et qui peut s\'en passer.',
        'Quelle base choisir selon ton projet (retraite, famille, télétravail, indépendant).',
      ],
      en: [
        'The 4 bases compared: climate, access, services, community.',
        'Rent/living costs for each town, multiple profiles.',
        'Healthcare, schools, paperwork: who depends on David and who can do without.',
        'Which base to choose by project (retirement, family, remote work, freelancing).',
      ],
      es: [
        'Las 4 bases comparadas: clima, acceso, servicios, comunidad.',
        'Costos de alquiler/vida para cada pueblo, varios perfiles.',
        'Salud, escuelas, trámites: quién depende de David y quién puede prescindir.',
        'Qué base elegir según tu proyecto (jubilación, familia, teletrabajo, freelance).',
      ],
    },
  },
  {
    slug: 'vivre-sur-la-cote-panama',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'vivre-sur-la-cote-panama-coronado-pedasi-bocas', en: 'lifestyle-cote-panama-coronado-pedasi-bocas', es: 'lifestyle-costa-panama-coronado-pedasi-bocas' } },
    price: 19,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-vivre-sur-la-cote-panama-coronado-pedasi-bocas.png',
    visible: true,
    focusKeyword: { fr: 'vivre côte panama', en: 'living panama coast', es: 'vivir costa panamá' },
    name: {
      fr: 'Vivre sur la côte — Coronado, Pedasí & Bocas',
      en: 'Living on Panama\'s Coast — Coronado, Pedasí & Bocas',
      es: 'Vivir en la costa de Panamá — Coronado, Pedasí y Bocas',
    },
    tagline: {
      fr: "Trois côtes, trois ambiances : Coronado le pratique près de la ville, Pedasí l'authentique sur le Pacifique sud, Bocas le caribéen. Comparées sans filtre.",
      en: 'Three coasts, three vibes: Coronado the practical near the city, Pedasí the authentic on the southern Pacific, Bocas the Caribbean. Compared without filter.',
      es: 'Tres costas, tres ambientes: Coronado práctico cerca de la ciudad, Pedasí auténtico en el Pacífico sur, Bocas caribeño. Comparados sin filtro.',
    },
    bullets: {
      fr: [
        'Coronado : accès Panama City, communauté, vrais loyers vs marketing.',
        'Pedasí : surf, plages, vie de village — qui s\'y plaît, qui s\'y ennuie.',
        'Bocas : archipel, expats, démarches insulaires (santé, école, internet).',
        'Comparatif loyer/vie + diagnostic « quelle côte pour toi ».',
      ],
      en: [
        'Coronado: access to Panama City, community, real rents vs marketing.',
        'Pedasí: surf, beaches, village life — who thrives, who gets bored.',
        'Bocas: archipelago, expats, island paperwork (health, school, internet).',
        'Rent/life comparison + diagnostic "which coast for you".',
      ],
      es: [
        'Coronado: acceso a Ciudad de Panamá, comunidad, alquileres reales vs marketing.',
        'Pedasí: surf, playas, vida de pueblo — quién prospera, quién se aburre.',
        'Bocas: archipiélago, expats, trámites isleños (salud, escuela, internet).',
        'Comparación alquiler/vida + diagnóstico "qué costa para ti".',
      ],
    },
  },
  {
    slug: 'el-valle-de-anton-guide',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'le-guide-el-valle-de-anton', en: 'el-valle-anton-guide', es: 'guide-el-valle-de-anton' } },
    price: 14,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-le-guide-el-valle-de-anton.png',
    visible: true,
    focusKeyword: { fr: 'el valle de anton panama', en: 'el valle de anton panama', es: 'el valle de antón panamá' },
    name: {
      fr: 'El Valle de Antón — guide expat',
      en: 'El Valle de Antón — Expat Guide',
      es: 'El Valle de Antón — guía expat',
    },
    tagline: {
      fr: 'Le cratère d\'altitude à 2 h de Panama City : climat doux, marché du dimanche, communauté discrète. La base alternative à Boquete pour qui veut rester proche de la ville.',
      en: 'The highland crater 2 h from Panama City: mild climate, Sunday market, low-key community. The alternative base to Boquete for those who want to stay near the city.',
      es: 'El cráter de altura a 2 h de Ciudad de Panamá: clima suave, mercado del domingo, comunidad discreta. La base alternativa a Boquete para quienes quieren quedarse cerca de la ciudad.',
    },
    bullets: {
      fr: [
        '12 lieux clés (logements, marché, randos, restos) — fiches détaillées.',
        'Coût de vie réel pour solo, couple, famille.',
        'Climat, accès, internet — testés sur place.',
        'La communauté locale et expat — comment se rendre utile vite.',
      ],
      en: [
        '12 key spots (housing, market, hikes, restaurants) — detailed cards.',
        'Real cost of living for single, couple, family.',
        'Climate, access, internet — tested on the ground.',
        'The local and expat community — how to become useful fast.',
      ],
      es: [
        '12 lugares clave (vivienda, mercado, senderos, restaurantes) — fichas detalladas.',
        'Costo de vida real para soltero, pareja, familia.',
        'Clima, acceso, internet — probados en el terreno.',
        'La comunidad local y expat — cómo volverse útil rápido.',
      ],
    },
  },
  {
    slug: 'pedasi-azuero-surf-plages',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'peninsule-pedasi-azuero', en: 'pedasi-azuero', es: 'pedasi-azuero-peninsula' } },
    price: 9,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-peninsule-pedasi-azuero.png',
    visible: true,
    focusKeyword: { fr: 'pedasi azuero panama', en: 'pedasi azuero panama', es: 'pedasí azuero panamá' },
    name: {
      fr: 'Pedasí & la péninsule d\'Azuero — surf & plages',
      en: 'Pedasí & the Azuero Peninsula — Surf & Beaches',
      es: 'Pedasí y la península de Azuero — surf y playas',
    },
    tagline: {
      fr: "Surf, plages sauvages, village authentique : le Pacifique sud côté nature. Plus calme que Bocas, plus pur que Coronado — pour qui sait pourquoi il vient.",
      en: 'Surf, wild beaches, authentic village: the southern Pacific, nature side. Calmer than Bocas, purer than Coronado — for those who know why they\'re coming.',
      es: 'Surf, playas salvajes, pueblo auténtico: el Pacífico sur lado natural. Más calmo que Bocas, más puro que Coronado — para quien sabe por qué viene.',
    },
    bullets: {
      fr: [
        '8 lieux : plages, spots de surf, restos, logements, services.',
        'Coût de vie + quand venir (saisons), accès depuis Panama City.',
        'Surf : niveaux, écoles, spots — pour débutants et confirmés.',
        'Ce que personne ne te dit sur l\'isolement à 4-5 h de la capitale.',
      ],
      en: [
        '8 spots: beaches, surf breaks, restaurants, housing, services.',
        'Cost of living + when to come (seasons), access from Panama City.',
        'Surf: levels, schools, breaks — for beginners and pros.',
        'What nobody tells you about being 4-5 h from the capital.',
      ],
      es: [
        '8 lugares: playas, spots de surf, restaurantes, vivienda, servicios.',
        'Costo de vida + cuándo venir (temporadas), acceso desde Ciudad de Panamá.',
        'Surf: niveles, escuelas, spots — principiantes y avanzados.',
        'Lo que nadie te dice sobre estar a 4-5 h de la capital.',
      ],
    },
  },
  {
    slug: 'panama-climat-frais-altitude',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'climat-tempere-panama', en: 'cool-climate-panama', es: 'clima-fresco-panama' } }, // fix 2026-07-04 : slugs FR/ES corrigés (les anciens 404aient sur Gumroad)
    price: 19,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-cool-climate-panama.png',
    visible: true,
    focusKeyword: { fr: 'panama climat frais altitude', en: 'cool climate panama highlands', es: 'panamá clima fresco altitud' },
    name: {
      fr: 'Panama climat frais — altitude & montagnes',
      en: 'Cool-Climate Panama — Highland & Altitude',
      es: 'Panamá clima fresco — altitud y montañas',
    },
    tagline: {
      fr: 'Les 5 bases d\'altitude du pays comparées — Boquete, Cerro Punta, Volcán, El Valle, Cerro Azul. Pour qui ne supporte pas la chaleur tropicale 24/7.',
      en: 'The country\'s 5 highland bases compared — Boquete, Cerro Punta, Volcán, El Valle, Cerro Azul. For those who can\'t take 24/7 tropical heat.',
      es: 'Las 5 bases de altura del país comparadas — Boquete, Cerro Punta, Volcán, El Valle, Cerro Azul. Para quienes no soportan el calor tropical 24/7.',
    },
    bullets: {
      fr: [
        'Les 5 bases : altitude exacte, températures saison sèche/humide.',
        'Climat micro-local — pourquoi Boquete et Cerro Punta n\'ont pas le même climat.',
        'Coût de vie comparé + accès médical par base.',
        'Comment décider entre montagne et bord de mer selon ton métabolisme.',
      ],
      en: [
        'The 5 bases: exact altitude, dry/wet season temperatures.',
        'Micro-local climate — why Boquete and Cerro Punta don\'t share the same.',
        'Cost of living compared + medical access per base.',
        'How to decide between highland and coastal based on your metabolism.',
      ],
      es: [
        'Las 5 bases: altitud exacta, temperaturas temporada seca/lluviosa.',
        'Clima microlocal — por qué Boquete y Cerro Punta no tienen el mismo.',
        'Costo de vida comparado + acceso médico por base.',
        'Cómo decidir entre montaña y costa según tu metabolismo.',
      ],
    },
  },
  {
    slug: 'cerro-azul-guide-expat',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'le-guide-expat-cerro-azul', en: 'cerro-azul-expat-guide', es: 'el-cerro-azul-expat-guide' } },
    price: 14,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-le-guide-expat-cerro-azul.png',
    visible: true,
    focusKeyword: { fr: 'cerro azul panama expat', en: 'cerro azul panama expat', es: 'cerro azul panamá expat' },
    name: {
      fr: 'Cerro Azul — guide expat',
      en: 'Cerro Azul — Expat Guide',
      es: 'Cerro Azul — guía expat',
    },
    tagline: {
      fr: 'La montagne à 1 h de Panama City — fraîcheur, calme, vue sur la baie. Le compromis pour qui veut l\'altitude sans s\'éloigner de la capitale.',
      en: 'The mountain 1 h from Panama City — cool, quiet, bay views. The compromise for those who want altitude without leaving the capital behind.',
      es: 'La montaña a 1 h de Ciudad de Panamá — frescor, calma, vista a la bahía. El compromiso para quienes quieren altura sin alejarse de la capital.',
    },
    bullets: {
      fr: [
        'Altitude, climat, accès depuis Panama City — concrètement.',
        'Loyers et achat immobilier sur place : ordre de grandeur + pièges.',
        'La communauté installée — petite mais structurée (résidentiels privés).',
        'Vie quotidienne : courses, médecin, internet — comment ça marche en montagne.',
      ],
      en: [
        'Altitude, climate, access from Panama City — concretely.',
        'Rents and buying property: ballpark + pitfalls.',
        'The settled community — small but structured (gated estates).',
        'Daily life: groceries, doctor, internet — how it works in the highlands.',
      ],
      es: [
        'Altitud, clima, acceso desde Ciudad de Panamá — concretamente.',
        'Alquileres y compra inmobiliaria: orden de magnitud + trampas.',
        'La comunidad instalada — pequeña pero estructurada (residenciales privados).',
        'Vida cotidiana: compras, médico, internet — cómo funciona en montaña.',
      ],
    },
  },
  // ============================================================
  // 2026-06-26 — V1 + V2 (suite : 12 produits supplémentaires)
  // Covers du manifest gumroad confirmées par script ; permalinks
  // FR/EN/ES vérifiés contre paymentLinks.js quand existants.
  // ============================================================
  {
    slug: 'quartiers-panama-city',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'quartiers', en: 'neighborhoods', es: 'barrios' } },
    price: 14,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-quartiers.png',
    visible: true,
    focusKeyword: { fr: 'quartiers panama city', en: 'panama city neighborhoods', es: 'barrios ciudad de panamá' },
    name: {
      fr: 'Quartiers de Panama City — les 15 décryptés',
      en: 'Panama City Neighborhoods — the 15 decoded',
      es: 'Barrios de Ciudad de Panamá — los 15 descifrados',
    },
    tagline: {
      fr: 'Les 15 quartiers de Panama City passés au crible : Casco, Marbella, San Francisco, Costa del Este, Punta Pacífica, Bella Vista, Obarrio, Albrook, Clayton, El Cangrejo… qui pour qui, pour quoi.',
      en: 'Panama City\'s 15 neighborhoods examined: Casco, Marbella, San Francisco, Costa del Este, Punta Pacífica, Bella Vista, Obarrio, Albrook, Clayton, El Cangrejo… who for who, for what.',
      es: 'Los 15 barrios de Ciudad de Panamá examinados: Casco, Marbella, San Francisco, Costa del Este, Punta Pacífica, Bella Vista, Obarrio, Albrook, Clayton, El Cangrejo… quién para quién.',
    },
    bullets: {
      fr: [
        'Loyers réels par typologie (studio à 3 ch) sur chaque quartier.',
        'Ambiance, accès, sécurité — fiche-rappel honnête de terrain.',
        'Pour qui : couple expat, retraité, famille, célibataire actif.',
        'Les pièges classiques (HOA, parking, métro, expat-bubble).',
      ],
      en: [
        'Real rents per typology (studio to 3-bed) for each neighborhood.',
        'Vibe, access, safety — honest field-tested cards.',
        'Who fits where: expat couple, retiree, family, busy single.',
        'Classic traps (HOA, parking, metro, expat bubble).',
      ],
      es: [
        'Alquileres reales por tipología (estudio a 3 hab) en cada barrio.',
        'Ambiente, acceso, seguridad — fichas honestas del terreno.',
        'Para quién: pareja expat, jubilado, familia, soltero activo.',
        'Las trampas clásicas (HOA, parking, metro, burbuja expat).',
      ],
    },
  },
  {
    slug: 'premier-voyage-panama',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'votre-premier-voyage-de-reperage-au-panama', en: 'first-scouting-trip-panama', es: 'tu-primer-viaje-de-reconocimiento-a-panama' } },
    price: 19,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-first-scouting-trip-panama.png',
    visible: true,
    focusKeyword: { fr: 'premier voyage panama reperage', en: 'first scouting trip panama', es: 'primer viaje panamá exploración' },
    name: {
      fr: 'Premier voyage — le repérage stratégique',
      en: 'First Scouting Trip to Panama',
      es: 'Primer viaje — la exploración estratégica',
    },
    tagline: {
      fr: 'Le voyage de repérage avant la décision : 10 jours bien utilisés pour tester quartiers, climat, école, hôpital — sans tomber dans le piège du touriste.',
      en: 'The pre-decision scouting trip: 10 days well spent to test neighborhoods, climate, schools, hospitals — without falling for the tourist trap.',
      es: 'El viaje de exploración antes de la decisión: 10 días bien usados para probar barrios, clima, escuelas, hospitales — sin caer en la trampa turística.',
    },
    bullets: {
      fr: [
        'L\'itinéraire 10 jours pour tester 3 bases possibles, pas pour cocher des touristic spots.',
        'Les rendez-vous à prendre AVANT de partir (banquier, agent immo, école).',
        'Les questions-pièges à poser sur place pour ne pas rentrer avec une fausse image.',
        'Budget repérage réaliste + checklist matériel (eSIM, Uber, cash, papiers).',
      ],
      en: [
        '10-day itinerary to test 3 possible bases, not to tick tourist spots.',
        'The appointments to book BEFORE you leave (banker, realtor, school).',
        'The trick-questions to ask on the ground so you don\'t leave with a fake picture.',
        'Realistic scouting budget + gear checklist (eSIM, Uber, cash, papers).',
      ],
      es: [
        'El itinerario de 10 días para probar 3 bases posibles, no para marcar lugares turísticos.',
        'Las citas a tomar ANTES de partir (banquero, agente inmobiliario, escuela).',
        'Las preguntas trampa para hacer en el terreno y no irte con falsa impresión.',
        'Presupuesto realista de exploración + checklist material (eSIM, Uber, cash, papeles).',
      ],
    },
  },
  {
    slug: 'setup-nomade-digital-panama',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'installation-nomade-numerique-au-panama', en: 'Digital-Nomad-Panama-Setup', es: 'configuracion-nomada-digital-en-panama' } },
    price: 49,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-azszoh.png',
    visible: true,
    focusKeyword: { fr: 'nomade digital panama setup', en: 'digital nomad panama setup', es: 'nómada digital panamá setup' },
    name: {
      fr: 'Setup nomade au Panama',
      en: 'Digital Nomad Panama Setup',
      es: 'Setup nómada digital en Panamá',
    },
    tagline: {
      fr: 'Le poste de travail nomade au Panama : 15 items (argent, fiscalité, coworking, eSIM, time zones, communauté) pour bosser depuis Panama City ou la plage sans casser ta facture.',
      en: 'The digital-nomad setup in Panama: 15 items (money, tax, coworking, eSIM, time zones, community) to work from Panama City or the beach without breaking your invoice.',
      es: 'El setup nómada digital en Panamá: 15 ítems (dinero, fiscalidad, coworking, eSIM, zonas horarias, comunidad) para trabajar desde Ciudad de Panamá o la playa sin romper tu factura.',
    },
    bullets: {
      fr: [
        'Argent : Wise, comptes multidevises, conversion FX, facturation client.',
        'Fiscalité nomade : convention, résidence fiscale, territorialité Panama.',
        'Poste de travail : coworking, internet, time zones (UTC-5 = EU matin = US déjeuner).',
        'Vie sociale nomade : communautés, événements, où rencontrer.',
      ],
      en: [
        'Money: Wise, multi-currency accounts, FX, client invoicing.',
        'Nomad tax: residency, treaty, Panama territoriality.',
        'Workstation: coworking, internet, time zones (UTC-5 = EU morning = US lunch).',
        'Nomad social life: communities, events, where to meet.',
      ],
      es: [
        'Dinero: Wise, cuentas multidivisa, FX, facturación clientes.',
        'Fiscalidad nómada: residencia, tratado, territorialidad Panamá.',
        'Estación de trabajo: coworking, internet, zonas horarias.',
        'Vida social nómada: comunidades, eventos, dónde conocer gente.',
      ],
    },
  },
  {
    slug: 'snowbird-couple-6-6',
    buy: { provider: 'gumroad', permalink: 'couple-6-6-snowbird' },
    price: 29,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-couple-6-6-snowbird.png',
    visible: false, // masquée 2026-10-18 : produit Gumroad NON publié (brouillon)
    focusKeyword: { fr: 'snowbird panama 6 mois', en: 'snowbird panama 6 months', es: 'snowbird panamá 6 meses' },
    name: {
      fr: 'Le couple 6/6 — 6 mois Panama, 6 mois France',
      en: 'The 6/6 Snowbird Couple — half Panama, half home',
      es: 'La pareja 6/6 — 6 meses Panamá, 6 meses Europa',
    },
    tagline: {
      fr: '6 mois ici, 6 mois là-bas : le mode de vie snowbird sans se faire piéger fiscalement, sans casser une assurance santé, sans louer deux fois.',
      en: '6 months here, 6 months there: the snowbird lifestyle without the tax trap, without breaking health insurance, without renting twice.',
      es: '6 meses aquí, 6 meses allá: el estilo snowbird sin trampa fiscal, sin romper el seguro de salud, sin alquilar dos veces.',
    },
    bullets: {
      fr: [
        'Le calendrier 6/6 qui te garde résident fiscal au Panama.',
        'Assurance santé qui suit (internationale vs locale + CFE).',
        'Logement : sous-location, gardiennage, AirBnB long terme.',
        'Banque, devises, courrier — les automatismes à mettre en place.',
      ],
      en: [
        'The 6/6 calendar that keeps you a Panama tax resident.',
        'Insurance that follows (international vs local + CFE).',
        'Housing: subletting, caretaking, long-term Airbnb.',
        'Banking, currency, mail — automatisms to set up.',
      ],
      es: [
        'El calendario 6/6 que te mantiene residente fiscal en Panamá.',
        'Seguro de salud que te sigue (internacional vs local + CFE).',
        'Vivienda: subarriendo, cuidado de casa, Airbnb largo plazo.',
        'Banca, divisas, correo — automatismos a configurar.',
      ],
    },
  },
  {
    slug: 'move-in-day-pack',
    buy: { provider: 'gumroad', permalink: 'move-in-panama' },
    price: 14,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-move-in-panama.png',
    visible: false, // masquée 2026-10-18 : produit Gumroad NON publié (brouillon)
    focusKeyword: { fr: 'arriver panama jour j', en: 'panama move-in day', es: 'llegar panamá día uno' },
    name: {
      fr: 'Move-In Day Pack — le jour J',
      en: 'Move-In Day Pack',
      es: 'Pack Día de Mudanza',
    },
    tagline: {
      fr: 'Le pack du jour de l\'arrivée : checklist douane Tocumen, transferts, premier logement, eSIM, ouverture banque — 24 heures organisées d\'avance.',
      en: 'The arrival-day pack: Tocumen customs checklist, transfers, first housing, eSIM, bank opening — 24 hours organized in advance.',
      es: 'El pack del día de llegada: checklist aduana Tocumen, transfers, primera vivienda, eSIM, apertura banco — 24 horas organizadas con antelación.',
    },
    bullets: {
      fr: [
        'Checklist douane Tocumen (objets autorisés, déclarations, animaux).',
        'Transfert aéroport → premier hébergement : 3 options chiffrées.',
        'eSIM activée avant atterrissage, banque appelable J1.',
        'Le sac de cabine indispensable (cash, documents, contacts).',
      ],
      en: [
        'Tocumen customs checklist (allowed items, declarations, pets).',
        'Airport → first housing transfer: 3 priced options.',
        'eSIM active before landing, bank reachable day 1.',
        'The essential carry-on (cash, documents, contacts).',
      ],
      es: [
        'Checklist aduana Tocumen (objetos permitidos, declaraciones, mascotas).',
        'Aeropuerto → primer alojamiento: 3 opciones con precio.',
        'eSIM activa antes de aterrizar, banco llamable día 1.',
        'El bolso de mano indispensable (efectivo, documentos, contactos).',
      ],
    },
  },
  {
    slug: 'permis-conduire-panama',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'permis-de-conduire-au-panama', en: 'driver-s-license-in-panama', es: 'licencia-de-conducir-en-panama' } },
    price: 24,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-permis-conduire-panama-guide.png',
    visible: true,
    focusKeyword: { fr: 'permis conduire panama', en: 'panama driving license', es: 'licencia conducir panamá' },
    name: {
      fr: 'Permis de conduire au Panama',
      en: 'Driving in Panama — License & ATTT',
      es: 'Licencia de conducir en Panamá',
    },
    tagline: {
      fr: 'De ton permis actuel au permis panaméen : ATTT, traduction, examen médical, échange ou test, plaques et amendes. Toutes les étapes documentées.',
      en: 'From your current license to the Panama one: ATTT, translation, medical exam, exchange or test, plates and fines. Every step documented.',
      es: 'De tu licencia actual a la panameña: ATTT, traducción, examen médico, canje o examen, placas y multas. Cada paso documentado.',
    },
    bullets: {
      fr: [
        'Échange direct ou nouveau passage : quand chacun s\'applique.',
        'Les démarches ATTT pas-à-pas (rendez-vous, médecins agréés, frais).',
        'Plaques et papiers du véhicule : ce qu\'il faut savoir avant de rouler.',
        'Amendes courantes et comment les éviter (alcootest, vitesse).',
      ],
      en: [
        'Direct exchange or new test: when each applies.',
        'ATTT process step-by-step (appointment, approved doctors, fees).',
        'Plates and vehicle papers: what to know before driving.',
        'Common fines and how to avoid them (DUI, speed).',
      ],
      es: [
        'Canje directo o nuevo examen: cuándo aplica cada uno.',
        'Trámites ATTT paso a paso (cita, médicos autorizados, tarifas).',
        'Placas y papeles del vehículo: lo que hay que saber antes de manejar.',
        'Multas comunes y cómo evitarlas (alcoholemia, velocidad).',
      ],
    },
  },
  {
    slug: 'premiere-voiture-panama',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'acheter-sa-premiere-voiture-au-panama', en: 'acheter-voiture-panama', es: 'comprar-tu-primer-auto-en-panama' } },
    price: 19,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-acheter-voiture-panama.png',
    visible: true,
    focusKeyword: { fr: 'acheter voiture panama', en: 'buying first car panama', es: 'comprar carro panamá' },
    name: {
      fr: 'Première voiture au Panama',
      en: 'Buying Your First Car in Panama',
      es: 'Primer carro en Panamá',
    },
    tagline: {
      fr: 'Acheter sa première voiture au Panama : neuf vs occasion, où chercher, comment vérifier un titre, assurance, immatriculation, taxes — sans se faire arnaquer.',
      en: 'Buying your first car in Panama: new vs used, where to look, how to verify a title, insurance, registration, taxes — without getting scammed.',
      es: 'Comprar tu primer carro en Panamá: nuevo vs usado, dónde buscar, cómo verificar un título, seguro, matrícula, impuestos — sin que te estafen.',
    },
    bullets: {
      fr: [
        'Neuf vs occasion : décote, fiabilité, coût total sur 3 ans.',
        'Vérifier un titre, un VIN, l\'historique d\'un véhicule au Panama.',
        'Assurance : tiers ou tous risques, quel courtier, les vraies primes.',
        'Immatriculation, taxes annuelles, contrôle technique (revisado).',
      ],
      en: [
        'New vs used: depreciation, reliability, 3-year total cost.',
        'How to verify a title, VIN, vehicle history in Panama.',
        'Insurance: third-party vs full coverage, which broker, real premiums.',
        'Registration, annual taxes, vehicle inspection (revisado).',
      ],
      es: [
        'Nuevo vs usado: depreciación, fiabilidad, costo total 3 años.',
        'Cómo verificar un título, VIN, historial del vehículo en Panamá.',
        'Seguro: terceros o todo riesgo, qué corredor, primas reales.',
        'Matrícula, impuestos anuales, revisado vehicular.',
      ],
    },
  },
  {
    slug: 'car-bundle-panama',
    buy: { provider: 'gumroad', permalink: 'car-bundle' },
    price: 34,
    tier: 'bundle',
    cover: '/product-covers/gumroad/cover-car-bundle.png',
    visible: true,
    focusKeyword: { fr: 'bundle voiture panama', en: 'panama car bundle', es: 'paquete carro panamá' },
    name: {
      fr: 'Car Bundle — Permis + Première voiture',
      en: 'Car Bundle — License + First Car',
      es: 'Paquete carro — licencia + primer auto',
    },
    tagline: {
      fr: 'Les 2 dossiers réunis : obtenir ton permis panaméen + acheter ta première voiture, sans angle mort. Économie vs les 2 séparés.',
      en: 'The 2 dossiers combined: get your Panama license + buy your first car, no blind spots. Discount vs the 2 separately.',
      es: 'Los 2 dossiers reunidos: obtener tu licencia panameña + comprar tu primer carro, sin puntos ciegos. Descuento vs los 2 por separado.',
    },
    bullets: {
      fr: [
        'Permis : échange/examen, ATTT, médecin agréé, frais.',
        'Achat : neuf vs occasion, vérif titre/VIN, assurance, taxes.',
        'Économie de bundle vs les 2 dossiers séparés.',
        'Bonus : checklist combinée + carnet d\'entretien type.',
      ],
      en: [
        'License: exchange/test, ATTT, approved doctor, fees.',
        'Purchase: new vs used, title/VIN check, insurance, taxes.',
        'Bundle savings vs the 2 separate files.',
        'Bonus: combined checklist + maintenance log template.',
      ],
      es: [
        'Licencia: canje/examen, ATTT, médico autorizado, tarifas.',
        'Compra: nuevo vs usado, verificación título/VIN, seguro, impuestos.',
        'Ahorro de paquete vs los 2 expedientes separados.',
        'Bonus: checklist combinada + plantilla bitácora mantenimiento.',
      ],
    },
  },
  {
    slug: 'importer-chien-panama',
    buy: { provider: 'gumroad', key: 'dog' },
    price: 14,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-chien-panama.png',
    // 2026-06-26 — DÉSACTIVÉ : Jade préfère relancer plus tard (risque
    // disclaimers vétérinaires/IPSA pas encore audité). Re-flip à true
    // quand contenu re-vérifié.
    visible: false,
    focusKeyword: { fr: 'importer chien panama', en: 'bring dog panama', es: 'traer perro panamá' },
    name: {
      fr: 'Importer ton chien au Panama',
      en: 'Bringing Your Dog to Panama',
      es: 'Traer tu perro a Panamá',
    },
    tagline: {
      fr: 'Le carnet officiel pour importer ton chien : permis IPSA, vaccins, microchip, certificat USDA/DGAV, cage IATA, douane Tocumen — du J-60 au J0.',
      en: 'The official playbook to import your dog: IPSA permit, vaccines, microchip, USDA/DGAV certificate, IATA crate, Tocumen customs — from D-60 to D0.',
      es: 'El manual oficial para traer tu perro: permiso IPSA, vacunas, microchip, certificado USDA/DGAV, jaula IATA, aduana Tocumen — del D-60 al D0.',
    },
    bullets: {
      fr: [
        'Permis IPSA : à demander 30-60 j avant, formulaire et coût exacts.',
        'Vaccins, microchip, certificat sanitaire : timing et vétos agréés.',
        'Cage IATA : dimensions, marquage, soute vs cabine.',
        'Douane Tocumen : déroulé arrivée, frais, ce qui peut bloquer.',
      ],
      en: [
        'IPSA permit: file 30-60 d before, exact form and cost.',
        'Vaccines, microchip, health certificate: timing and approved vets.',
        'IATA crate: dimensions, labeling, hold vs cabin.',
        'Tocumen customs: arrival walkthrough, fees, what can block.',
      ],
      es: [
        'Permiso IPSA: solicitar 30-60 d antes, formulario y costo exactos.',
        'Vacunas, microchip, certificado sanitario: timing y veterinarios autorizados.',
        'Jaula IATA: dimensiones, marcado, bodega vs cabina.',
        'Aduana Tocumen: recorrido a la llegada, tarifas, qué puede bloquear.',
      ],
    },
  },
  {
    slug: 'dog-bundle-panama',
    buy: { provider: 'gumroad', key: 'dogBundle' },
    price: 29,
    tier: 'bundle',
    cover: '/product-covers/gumroad/cover-bundle-chien-panama.png',
    // 2026-06-26 — DÉSACTIVÉ (idem chien) : re-flip à true plus tard.
    visible: false,
    focusKeyword: { fr: 'bundle chien panama complet', en: 'panama dog bundle', es: 'paquete perro panamá' },
    name: {
      fr: 'Bundle Chien — Carnet + Checklist + Q&A',
      en: 'Dog Bundle — Playbook + Checklist + Q&A',
      es: 'Paquete Perro — Manual + Checklist + Q&A',
    },
    tagline: {
      fr: 'Le carnet complet + une checklist A4 imprimable + 25 Q&A des cas vécus. Le pack que voulaient les familles avec animal qui débarquent au Panama.',
      en: 'The full playbook + a printable A4 checklist + 25 Q&A of real cases. The pack families with pets landing in Panama wanted.',
      es: 'El manual completo + checklist A4 imprimible + 25 Q&A de casos reales. El pack que las familias con mascota que llegan a Panamá querían.',
    },
    bullets: {
      fr: [
        'Le carnet complet (importer chien) — 100 % inclus.',
        'Checklist A4 imprimable, à cocher en imprimerie de quartier.',
        '25 Q&A de cas réels : Air France Cargo, KLM, transit Madrid, IPSA refus.',
        'Bonus : annuaire vétos agréés + clinique 24/7 Panama City.',
      ],
      en: [
        'The full playbook (bringing your dog) — 100% included.',
        'Printable A4 checklist for your local print shop.',
        '25 Q&A of real cases: Air France Cargo, KLM, Madrid transit, IPSA refusals.',
        'Bonus: approved vet directory + 24/7 clinic Panama City.',
      ],
      es: [
        'El manual completo (traer perro) — 100 % incluido.',
        'Checklist A4 imprimible para tu copistería local.',
        '25 Q&A de casos reales: Air France Cargo, KLM, tránsito Madrid, IPSA rechazos.',
        'Bonus: directorio veterinarios autorizados + clínica 24/7 Ciudad de Panamá.',
      ],
    },
  },
  {
    slug: 'panama-7-jours-itineraire',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'le-panama-en-7-jours', en: 'panama-7-days', es: 'panama-en-7-dias' } },
    price: 12,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-panama-7-days.png',
    visible: true,
    focusKeyword: { fr: 'panama 7 jours itineraire', en: 'panama 7 day itinerary', es: 'panamá 7 días itinerario' },
    name: {
      fr: 'Le Panama en 7 jours — l\'itinéraire flagship',
      en: 'Panama in 7 Days — the flagship itinerary',
      es: 'Panamá en 7 días — el itinerario flagship',
    },
    tagline: {
      fr: 'Une semaine bien menée : Casco, Canal, San Blas, Boquete ou Pedasí — 8 étapes optimisées, hôtels, transferts, budgets. Pour ceux qui ont 7 jours, pas 21.',
      en: 'A well-run week: Casco, Canal, San Blas, Boquete or Pedasí — 8 optimized steps, hotels, transfers, budgets. For those who have 7 days, not 21.',
      es: 'Una semana bien llevada: Casco, Canal, San Blas, Boquete o Pedasí — 8 etapas optimizadas, hoteles, transfers, presupuestos. Para quienes tienen 7 días, no 21.',
    },
    bullets: {
      fr: [
        '8 étapes Jour 1 → Jour 7 (Casco, Canal, San Blas, Boquete/Pedasí).',
        'Hôtels conseillés par budget (économique, moyen, premium).',
        'Transferts internes (vols, ferry, voiture) — prix réels.',
        'Budget global + erreurs courantes (saison sèche, jours fériés, fermetures).',
      ],
      en: [
        '8 steps Day 1 → Day 7 (Casco, Canal, San Blas, Boquete/Pedasí).',
        'Recommended hotels by budget (low, mid, premium).',
        'Internal transfers (flights, ferry, car) — real prices.',
        'Total budget + common mistakes (dry season, holidays, closures).',
      ],
      es: [
        '8 etapas Día 1 → Día 7 (Casco, Canal, San Blas, Boquete/Pedasí).',
        'Hoteles recomendados por presupuesto (económico, medio, premium).',
        'Transfers internos (vuelos, ferry, carro) — precios reales.',
        'Presupuesto total + errores comunes (temporada seca, feriados, cierres).',
      ],
    },
  },
  {
    slug: 'party-panama-nightlife',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'vie-nocturne-panama', en: 'party-panama', es: 'fiesta-panama' } },
    price: 12,
    tier: 'memo',
    cover: '/product-covers/gumroad/cover-party-panama.png',
    visible: true,
    focusKeyword: { fr: 'panama nightlife after dark', en: 'panama nightlife party', es: 'panamá vida nocturna' },
    name: {
      fr: 'Party Panama — la nuit & sorties',
      en: 'Party Panama — Nightlife & Vacation',
      es: 'Party Panama — vida nocturna',
    },
    tagline: {
      fr: 'Panama after dark : Casco, Calle Uruguay, Marbella — bars, clubs, rooftops, spectacles. Les bonnes adresses, les heures, les codes vestimentaires, ce qu\'on évite.',
      en: 'Panama after dark: Casco, Calle Uruguay, Marbella — bars, clubs, rooftops, shows. The good addresses, the hours, the dress codes, what to avoid.',
      es: 'Panamá after dark: Casco, Calle Uruguay, Marbella — bares, clubes, rooftops, shows. Las buenas direcciones, las horas, los códigos de vestimenta, lo que se evita.',
    },
    bullets: {
      fr: [
        'Casco la nuit : bars à mezcal, rooftops, jazz live.',
        'Calle Uruguay & Marbella : clubs, dress code, prix d\'entrée.',
        'Sécurité nocturne : Uber, quartiers à éviter après minuit.',
        'Le calendrier nightlife + festivals annuels à connaître.',
      ],
      en: [
        'Casco at night: mezcal bars, rooftops, live jazz.',
        'Calle Uruguay & Marbella: clubs, dress code, cover charges.',
        'Night safety: Uber, neighborhoods to avoid after midnight.',
        'Nightlife calendar + annual festivals to know.',
      ],
      es: [
        'Casco de noche: bares de mezcal, rooftops, jazz en vivo.',
        'Calle Uruguay y Marbella: clubes, código de vestimenta, cover.',
        'Seguridad nocturna: Uber, barrios a evitar pasada la medianoche.',
        'Calendario nightlife + festivales anuales que hay que conocer.',
      ],
    },
  },
  {
    slug: 'pack-modeles-expat',
    buy: { provider: 'gumroad', permalinkByLang: { fr: 'pack-de-modeles-pour-expatries-au-panama', en: 'templates-pack', es: 'templates-pack' } },
    price: 29,
    tier: 'toolkit',
    cover: '/product-covers/gumroad/cover-templates-pack.png',
    visible: true,
    focusKeyword: { fr: 'pack modeles expat panama', en: 'panama expat templates', es: 'plantillas expat panamá' },
    name: {
      fr: 'Pack Modèles Expat — 30 documents prêts',
      en: 'Expat Templates Pack — 30 ready docs',
      es: 'Pack Plantillas Expat — 30 documentos listos',
    },
    tagline: {
      fr: '30 modèles de courriers et formulaires prêts à envoyer : avocat, banque, bailleur, IPSA, ATTT, école, mutuelle. FR + ES, à personnaliser et envoyer.',
      en: '30 ready-to-send letter and form templates: lawyer, bank, landlord, IPSA, ATTT, school, insurance. FR + ES, to personalize and send.',
      es: '30 plantillas de cartas y formularios listas para enviar: abogado, banco, arrendador, IPSA, ATTT, escuela, seguro. FR + ES, para personalizar y enviar.',
    },
    bullets: {
      fr: [
        '30 modèles couvrant 7 domaines (visa, banque, immo, école, santé, animal, voiture).',
        'Versions FR + ES (Word + PDF), à compléter sur place.',
        'Notes en marge : quoi remplacer, quoi laisser tel quel, qui signer.',
        'Bonus : mini-glossaire termes administratifs FR-ES.',
      ],
      en: [
        '30 templates covering 7 areas (visa, bank, real estate, school, health, pet, car).',
        'FR + ES versions (Word + PDF), to complete on the ground.',
        'Margin notes: what to replace, what to leave, who signs.',
        'Bonus: mini glossary of FR-ES admin terms.',
      ],
      es: [
        '30 plantillas cubriendo 7 áreas (visa, banco, inmobiliario, escuela, salud, mascota, carro).',
        'Versiones FR + ES (Word + PDF), para completar en el terreno.',
        'Notas al margen: qué reemplazar, qué dejar, quién firma.',
        'Bonus: mini glosario términos administrativos FR-ES.',
      ],
    },
  },
  {
    slug: 'expat-survival-kit-1re-annee',
    buy: { provider: 'gumroad', permalink: 'expat-survival-kit' },
    price: 79,
    tier: 'bundle',
    cover: '/product-covers/gumroad/cover-expat-survival-kit.png',
    visible: false, // masquée 2026-10-18 : produit Gumroad NON publié (brouillon)
    focusKeyword: { fr: 'survival kit expat panama 1ere annee', en: 'expat survival kit panama first year', es: 'kit supervivencia expat panamá primer año' },
    name: {
      fr: 'Expat Survival Kit — la 1ère année',
      en: 'Expat Survival Kit Bundle — Year 1',
      es: 'Kit Supervivencia Expat — el primer año',
    },
    tagline: {
      fr: 'Le bundle des 12 premiers mois : Setup 30j + Quartiers + Pack Modèles + santé + permis + carnet d\'astuces. L\'arsenal complet pour réussir ta 1ère année.',
      en: 'The first-12-months bundle: Setup 30d + Neighborhoods + Templates Pack + healthcare + license + tips notebook. The full arsenal to make year 1 work.',
      es: 'El paquete de los primeros 12 meses: Setup 30d + Barrios + Pack Plantillas + salud + licencia + cuaderno de trucos. El arsenal completo para tu primer año.',
    },
    bullets: {
      fr: [
        '5 produits clés inclus + 1 carnet d\'astuces inédit (90 pages).',
        'Économie : -35 % vs achat séparé de chaque dossier.',
        'Ordre conseillé d\'exploitation, mois par mois (M1 → M12).',
        'Bonus : accès Q&A trimestriel pour clients du bundle (1 an).',
      ],
      en: [
        '5 key products included + 1 unreleased tips notebook (90 pages).',
        'Savings: -35% vs buying each dossier separately.',
        'Recommended order of use, month by month (M1 → M12).',
        'Bonus: quarterly Q&A access for bundle clients (1 year).',
      ],
      es: [
        '5 productos clave incluidos + 1 cuaderno de trucos inédito (90 páginas).',
        'Ahorro: -35 % vs compra separada de cada expediente.',
        'Orden recomendado de uso, mes por mes (M1 → M12).',
        'Bonus: acceso Q&A trimestral para clientes del paquete (1 año).',
      ],
    },
  },
];

/** Produit par slug, ou null. */
export function getProduct(slug) {
  return PRODUCTS.find((p) => p.slug === slug) || null;
}

/** Produits visibles (listés dans /shop + indexés). */
export const VISIBLE_PRODUCTS = PRODUCTS.filter((p) => p.visible);

/** i18n helper — valeur dans la langue, fallback FR. */
export function pick(obj, lang) {
  if (!obj || typeof obj !== 'object') return obj;
  return obj[lang] ?? obj.fr ?? Object.values(obj)[0];
}
